import React, { useEffect, useState } from "react";

const SUITS = [
  {
    id: "classic",
    name: "Classic Red",
    tag: "The web-slinger",
    // Base card: navy → red web
    bg: "linear-gradient(135deg, #061638 0%, #0A1D4F 45%, #7C1218 100%)",
    accent: "#DC1F26",
    keyframe: "trailer-web-ripple",
    svg: (
      <svg viewBox="0 0 200 200" fill="none" className="absolute inset-0 w-full h-full">
        <g stroke="#DC1F26" strokeWidth="0.9" opacity="0.85">
          <circle cx="100" cy="100" r="20" />
          <circle cx="100" cy="100" r="38" />
          <circle cx="100" cy="100" r="60" />
          <circle cx="100" cy="100" r="86" />
          <line x1="100" y1="14" x2="100" y2="186" />
          <line x1="14" y1="100" x2="186" y2="100" />
          <line x1="35" y1="35" x2="165" y2="165" />
          <line x1="165" y1="35" x2="35" y2="165" />
        </g>
      </svg>
    ),
  },
  {
    id: "symbiote",
    name: "Symbiote Black",
    tag: "Feel the bond",
    bg: "linear-gradient(135deg, #050505 0%, #1F1F1F 45%, #3F3F46 100%)",
    accent: "#F1F5F9",
    keyframe: "trailer-ink-drip",
    svg: (
      <svg viewBox="0 0 200 200" fill="none" className="absolute inset-0 w-full h-full">
        <defs>
          <radialGradient id="ink1" cx="30%" cy="0%" r="60%">
            <stop offset="0%" stopColor="#0A0A0A" stopOpacity="1" />
            <stop offset="70%" stopColor="#0A0A0A" stopOpacity="0" />
          </radialGradient>
          <radialGradient id="ink2" cx="70%" cy="10%" r="55%">
            <stop offset="0%" stopColor="#0A0A0A" stopOpacity="1" />
            <stop offset="70%" stopColor="#0A0A0A" stopOpacity="0" />
          </radialGradient>
        </defs>
        <rect width="200" height="200" fill="url(#ink1)" />
        <rect width="200" height="200" fill="url(#ink2)" />
        <g stroke="rgba(255,255,255,0.4)" strokeWidth="0.6">
          <path d="M20 40 Q 60 60 100 45 T 180 60" fill="none" />
          <path d="M30 90 Q 70 110 110 95 T 175 105" fill="none" />
        </g>
      </svg>
    ),
  },
  {
    id: "miles",
    name: "Miles Morales",
    tag: "Brooklyn stitched",
    bg: "linear-gradient(135deg, #0B0620 0%, #1E1B4B 45%, #7C0F32 100%)",
    accent: "#E11D48",
    keyframe: "trailer-electric",
    svg: (
      <svg viewBox="0 0 200 200" fill="none" className="absolute inset-0 w-full h-full">
        <g stroke="#A855F7" strokeWidth="1.4" strokeLinecap="round" opacity="0.95">
          <path d="M15 100 Q 50 75 75 95 T 125 85 T 175 100 T 195 90" fill="none" />
          <path d="M20 130 Q 55 110 85 125 T 140 115 T 190 130" fill="none" />
          <path d="M25 70 Q 60 55 90 75 T 135 60 T 190 70" fill="none" />
        </g>
        <g stroke="#E11D48" strokeWidth="1" strokeLinecap="round" opacity="0.8">
          <path d="M20 105 Q 60 85 90 105 T 140 90 T 185 100" fill="none" />
        </g>
      </svg>
    ),
  },
];

export default function SuitTrailer() {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI(p => (p + 1) % SUITS.length), 2400);
    return () => clearInterval(t);
  }, []);
  const active = SUITS[i];

  return (
    <div data-testid="suit-trailer" className="mt-8">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-[10px] tracking-widest uppercase font-bold text-slate-500 dark:text-slate-400">
          Three suits, three moods
        </span>
        <span className="h-px flex-1 bg-slate-200 dark:bg-slate-800" />
      </div>

      <div className="grid grid-cols-3 gap-2 sm:gap-3">
        {SUITS.map((s, idx) => (
          <button
            key={s.id}
            type="button"
            onClick={() => setI(idx)}
            aria-label={`Preview ${s.name}`}
            data-testid={`trailer-tile-${s.id}`}
            className={`relative rounded-2xl overflow-hidden border transition-all duration-300 aspect-[4/3] group ${
              idx === i
                ? "border-slate-800 dark:border-white/30 shadow-lg shadow-slate-900/20 scale-[1.02]"
                : "border-slate-200 dark:border-slate-800 hover:border-slate-400 dark:hover:border-slate-600 opacity-70 hover:opacity-100"
            }`}
            style={{ background: s.bg }}
          >
            {/* SVG signature layer — animates only while this tile is active */}
            <div
              className={`absolute inset-0 ${idx === i ? "opacity-100" : "opacity-40"}`}
              style={{
                animation: idx === i ? `${s.keyframe} 2200ms ease-in-out infinite` : "none",
                transformOrigin: "center",
              }}
            >
              {s.svg}
            </div>
            {/* Label pinned bottom-left */}
            <div className="absolute left-2 right-2 bottom-1.5 text-left">
              <div className="text-[10px] sm:text-xs font-bold text-white truncate drop-shadow" style={{ textShadow: "0 1px 2px rgba(0,0,0,0.6)" }}>
                {s.name}
              </div>
              <div className="text-[9px] text-white/75 truncate" style={{ textShadow: "0 1px 2px rgba(0,0,0,0.6)" }}>
                {s.tag}
              </div>
            </div>
            {/* Active dot */}
            {idx === i && (
              <div className="absolute top-2 right-2 w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: s.accent, boxShadow: `0 0 8px ${s.accent}` }} />
            )}
          </button>
        ))}
      </div>

      <p className="mt-3 text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
        Pick a suit in Settings after you sign in. Every button, hero, and animation reshapes to match your mood.
      </p>

      {/* Trailer keyframes — inline so the component is self-contained. */}
      <style>{`
        @keyframes trailer-web-ripple {
          0%   { transform: scale(0.9); opacity: 0.55; }
          50%  { transform: scale(1.1); opacity: 1; }
          100% { transform: scale(0.9); opacity: 0.55; }
        }
        @keyframes trailer-ink-drip {
          0%   { transform: translateY(-6%); opacity: 0.65; }
          50%  { transform: translateY(6%);  opacity: 1; }
          100% { transform: translateY(-6%); opacity: 0.65; }
        }
        @keyframes trailer-electric {
          0%, 100% { opacity: 0.95; filter: drop-shadow(0 0 4px rgba(168,85,247,0.6)); }
          25%      { opacity: 0.5;  filter: drop-shadow(0 0 2px rgba(168,85,247,0.3)); }
          50%      { opacity: 1;    filter: drop-shadow(0 0 8px rgba(168,85,247,0.9)); }
          75%      { opacity: 0.65; filter: drop-shadow(0 0 3px rgba(168,85,247,0.4)); }
        }
      `}</style>
    </div>
  );
}
